import React, { useState, useEffect } from 'react';
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { Panel } from 'primereact/panel';
import { Link } from 'react-router-dom';
import { useSearchParams } from "react-router-dom";
import { SERVER_HOST } from '../settings';
import { Tooltip } from 'primereact/tooltip';
import { countOfGeoRankPointsAwardedPlayers } from './util/geoRankPoints';
import { displayGameMode, displayRankingType } from './util/displayNames';

export default function Ranking({gameMode, rankingType}) {
    const [customers, setCustomers] = useState(null);
    const [filters, setFilters] = useState({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        name: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
        countryName: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
        nick: { value: null, matchMode: FilterMatchMode.IN }
    });

    const [loading, setLoading] = useState(true);
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [searchParams, setSearchParams] = useSearchParams();
    const [totalRecords, setTotalRecords] = useState(0);

    const [lazyState, setLazyState] = useState({
        first: 0,
        rows: 25,
        page: 1,
        sortField: searchParams.get('sortField') || 'globalPosition',
        sortOrder: searchParams.get('sortOrder') || '1',
        filters: {
            points: { value: '', matchMode: 'contains' },
            countryName: { value: searchParams.get("countryName") || "", matchMode: 'contains' },
            rating: { value: '', matchMode: 'contains' },
            nick: { value: searchParams.get("nick"), matchMode: 'contains' }
        }
    });

    useEffect(() => {
        const newCountryName = lazyState?.filters?.countryName?.value;
        const newNick = lazyState?.filters?.nick?.value;
        const newSortField = lazyState?.sortField;
        const newSortOrder = lazyState?.sortOrder;
        const params = {};
        if (newCountryName) {
            params.countryName = newCountryName;
        }
        if (newNick) {
            params.nick = newNick;
        }
        if (rankingType) {
            params.rankingType = rankingType;
        }
        if (gameMode) {
            params.gameMode = gameMode;
        }
        if (newSortField) {
            params.sortField = newSortField;
        }
        if (newSortOrder) {
            params.sortOrder = newSortOrder;
        }
        setSearchParams(params);
      }, [lazyState, gameMode, rankingType]);

    let networkTimeout = null;

    const loadLazyData = () => {
        setLoading(true);

        if (networkTimeout) {
            clearTimeout(networkTimeout);
        }

        const queryParams = {
            ...lazyState,
            filters: JSON.stringify(lazyState.filters),
            ratingType: gameMode,
            rankingType
        };
        const encodeQueryParams = (queryParams) => {
            return Object.entries(queryParams).map(([key, value]) => `${key}=${value}`).join('&');
        };
        fetch(`${SERVER_HOST}/playersranking?${encodeQueryParams(queryParams)}`).then(data => data.json()).then(data => {
            // console.log(data);
            setTotalRecords(data.totalRecords);
            setCustomers(data.records);
            setLoading(false);
        })
    };

    useEffect(() => {
        loadLazyData();
    }, [lazyState, rankingType]);

    const onPage = (event) => {
        setLazyState(event);
    };

    const onSort = (event) => {
        setLazyState(event);
    };

    const onFilter = (event) => {
        event['first'] = 0;
        setLazyState(event);
    };

    const onGlobalFilterChange = (e) => {
        const value = e.target.value;
        let _filters = { ...filters };

        _filters['global'].value = value;

        setFilters(_filters);
        setGlobalFilterValue(value);
    };

    const countryBodyTemplate = (rowData) => {
        return (
            <div className="flex align-items-center gap-2">
                <img src={`./flags/${rowData.countryCode}.png`} style={{ width: '24px' }} />
                <img alt="flag" src="https://primefaces.org/cdn/primereact/images/flag/flag_placeholder.png" className={`flag flag-${rowData.countryCode}`} style={{ width: '24px' }} />
                <span>{rowData.countryName} ({rowData.countryPosition})</span>
            </div>
        );
    };

    const playerBodyTemplate = (rowData) => {
        const player = rowData.nick;

        return (
            <Link to={`/player/${rowData.userId}`} className="flex align-items-center gap-2">
                <div className="avatar-wrapper">
                    <div className="avatar">
                        <img className="avatar-img" src={`https://www.geoguessr.com/images/resize:auto:144:144/gravity:ce/plain/pin/${rowData.fullBodyPath}.png`}/>
                    </div>
                </div>
                <span>{player}</span>
            </Link>
        );
    };

    const numberRowFilterTemplate = (options) => {
        const [from, to] = options.value ?? [null, null];        

        return (
            <div className="flex gap-1">
                <InputNumber value={from} onChange={(e) => {console.log('filter', e); options.filterApplyCallback([e.value, to]);}} className="w-full" placeholder="from" />
                <InputNumber value={to} onChange={(e) => options.filterApplyCallback([from, e.value])} className="w-full" placeholder="to" />
            </div>
        );
    };

    const countryFilterTemplate = (options) => {
        // console.log(options);
        return (
            <div className="flex gap-1">
                <InputText value={options.value} onChange={(e) => {console.log('e', e); options.filterApplyCallback(e.target.value);}} className="w-full" placeholder={`Search by ${options.field}`} />
            </div>
        );
    };

    const geoPointsHeader = (
        <span>
            <span>GeoRank Points</span>
            &nbsp;&nbsp;
            <span>
                <Tooltip target={`.georank-points-info`} mouseTrack mouseTrackLeft={10}/>
                <i className='pi pi-info-circle georank-points-info' data-pr-tooltip={`${countOfGeoRankPointsAwardedPlayers(rankingType, gameMode)} players awarded 'GeoRank points' in ${displayRankingType(rankingType)} ranking in ${displayGameMode(gameMode)} game mode`}></i>
            </span>
        </span>
    )

    return (
        <div className="grid">
            <div className="card col-12 xl:col-10 xl:col-offset-1">
                <DataTable value={customers} stripedRows paginator rows={lazyState.rows} rowsPerPageOptions={[10, 25, 50]} dataKey="userId" filterDisplay="row" loading={loading}
                        globalFilterFields={['nick', 'countryName']} emptyMessage="No customers found." lazy totalRecords={totalRecords} onPage={onPage}
                        first={lazyState.first} onSort={onSort} sortField={lazyState.sortField} sortOrder={lazyState.sortOrder}
                        onFilter={onFilter} filters={lazyState.filters} >
                    <Column field="globalPosition" header="Pos." sortable />
                    <Column header="Player" filterField="nick" showFilterMenu={false}  style={{ minWidth: '16rem' }} filterElement={countryFilterTemplate}
                        body={playerBodyTemplate} filter filterPlaceholder='Search by player name'/>
                    <Column header="Country" filterField="countryName" showFilterMenu={false} style={{ minWidth: '16rem' }} body={countryBodyTemplate} filter filterElement={countryFilterTemplate} filterPlaceholder="Search by country" />
                    <Column field="rating" header="Rating" filter sortable showFilterMenu={false} style={{ minWidth: '14rem' }} filterElement={numberRowFilterTemplate} />
                    <Column field="points" header={geoPointsHeader} filter sortable showFilterMenu={false} style={{ minWidth: '14rem' }} filterElement={numberRowFilterTemplate} />
                </DataTable>
            </div>
        </div>
    );
}
        